import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import Breadcrumb from './Breadcrumb';
import SEOHead from './SEOHead';

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const CaseStudyTemplate = ({
  seoTitle,
  seoDescription,
  canonical,
  client,
  headline,
  sector,
  typeOfWork,
  intro,
  sections,
  quotes,
  mainLesson,
  cta,
}) => {
  return (
    <>
      <SEOHead title={seoTitle} description={seoDescription} canonical={canonical} />

      {/* Hero banner */}
      <section className="bg-carbon relative overflow-hidden py-20 md:py-28">
        <img
          src="/assets/kalobrate-icon-outline-yellow.png"
          alt="" aria-hidden="true"
          className="absolute top-[-10%] right-[-5%] w-[55%] h-[120%] object-contain opacity-[0.06] pointer-events-none select-none"
        />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb items={[{ label: 'Results', href: '/results' }, { label: client }]} />
          <motion.div {...fadeIn}>
            <span className="inline-block bg-golden text-carbon text-xs font-semibold px-3 py-1 mb-4">{typeOfWork}</span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight tracking-tight mb-6">
              {headline}
            </h1>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60">
              <span><strong className="text-white/80">Client:</strong> {client}</span>
              <span><strong className="text-white/80">Sector:</strong> {sector}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <article className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Introduction */}
          {intro && (
            <motion.div className="mb-12" {...fadeIn}>
              {intro.map((p, i) => (
                <p key={i} className="text-base text-carbon/80 leading-relaxed mb-4">{p}</p>
              ))}
            </motion.div>
          )}

          {/* Content sections */}
          {sections.map((section, i) => (
            <motion.section
              key={i}
              className="mb-14"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-xl sm:text-2xl font-heading font-bold text-carbon mb-5 tracking-tight">
                {section.heading}
              </h2>
              {section.content.map((block, j) => {
                if (typeof block === 'string') {
                  return <p key={j} className="text-base text-carbon/80 leading-relaxed mb-4">{block}</p>;
                }
                if (block.type === 'list') {
                  return (
                    <ul key={j} className="space-y-2 mb-4 ml-1">
                      {block.items.map((item, k) => (
                        <li key={k} className="flex items-start gap-3 text-sm text-carbon/75 leading-relaxed">
                          <span className="w-1.5 h-1.5 bg-golden rounded-full mt-2 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                if (block.type === 'stats') {
                  return (
                    <div key={j} className="grid grid-cols-2 sm:grid-cols-4 gap-4 my-8">
                      {block.items.map((stat, k) => (
                        <div key={k} className="bg-gray-50 border border-gray-100 p-4 text-center">
                          <span className="text-2xl font-heading font-bold text-carbon block">{stat.value}</span>
                          <span className="text-xs text-carbon/60">{stat.label}</span>
                        </div>
                      ))}
                    </div>
                  );
                }
                return null;
              })}
            </motion.section>
          ))}

          {/* Pull quotes */}
          {quotes && quotes.length > 0 && (
            <motion.section
              className="mb-14"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-xl sm:text-2xl font-heading font-bold text-carbon mb-6 tracking-tight">
                Client feedback
              </h2>
              <div className="space-y-6">
                {quotes.map((q, i) => (
                  <blockquote key={i} className="border-l-4 border-golden pl-6 py-2">
                    <p className="text-base text-carbon/85 leading-relaxed italic mb-3">"{q.text}"</p>
                    <footer className="text-sm">
                      <span className="font-semibold text-carbon">{q.name}</span>
                      <span className="text-carbon/50 block">{q.role}</span>
                    </footer>
                  </blockquote>
                ))}
              </div>
            </motion.section>
          )}

          {/* Main lesson */}
          {mainLesson && (
            <motion.section
              className="mb-14 bg-gray-50 border border-gray-100 p-6 md:p-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-lg font-heading font-bold text-carbon mb-3">The main lesson</h2>
              {mainLesson.map((p, i) => (
                <p key={i} className="text-sm text-carbon/80 leading-relaxed mb-2 last:mb-0">{p}</p>
              ))}
            </motion.section>
          )}

          {/* Back link */}
          <div className="mb-14">
            <Link to="/results" className="inline-flex items-center text-sm font-semibold text-carbon hover:text-golden transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              View all results
            </Link>
          </div>
        </div>
      </article>

      {/* CTA */}
      {cta && (
        <section className="bg-golden py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-carbon mb-4 tracking-tight">{cta.heading}</h2>
            <p className="text-base text-carbon/80 mb-8 max-w-xl mx-auto leading-relaxed">{cta.copy}</p>
            <Link
              to="/contact"
              className="bg-carbon text-white px-7 py-3.5 font-semibold text-sm hover:bg-carbon/90 transition-colors inline-flex items-center"
            >
              {cta.button || 'Talk to Ollie'}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </section>
      )}
    </>
  );
};

export default CaseStudyTemplate;
